import { useState } from "react";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import {
  useAdminRateLimits,
  type AppRateLimit,
  type AppRateLimitPair,
  type RateLimitState,
  type RateLimitTenant,
  type RateLimitWindowLimit,
} from "../../hooks/useAdminRateLimits";
import styles from "./AdminRateLimits.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

const formatNumber = (n: number): string => n.toLocaleString();
const formatLimit = (n: number | null): string => (n === null ? "—" : n.toLocaleString());

// Cold-load skeleton headers, before the response tells us the live window set.
const WINDOW_HEADERS: ReadonlyArray<string> = ["12h", "48h", "7d", "30d"];

const STATE_LABEL: Record<RateLimitState, string> = {
  ok: "OK",
  alert: "Alerting",
  block: "Blocked",
};

const textClass = (state: RateLimitState): string =>
  state === "block" ? styles.block ?? "" : state === "alert" ? styles.alert ?? "" : styles.ok ?? "";

const badgeClass = (state: RateLimitState): string =>
  state === "block"
    ? styles.badgeBlock ?? ""
    : state === "alert"
      ? styles.badgeAlert ?? ""
      : styles.badgeOk ?? "";

export const AdminRateLimits = (): JSX.Element => {
  const { status, data, error, isFetching, refresh, updateWindow, updateAppWindow, deleteApp } =
    useAdminRateLimits();
  const windows = data?.windows ?? [];
  const tenants = data?.tenants ?? [];
  const apps = data?.apps ?? [];
  const appPairs = data?.appPairs ?? [];
  const blocked = tenants.filter((t) => t.worstState === "block").length;
  const alerting = tenants.filter((t) => t.worstState === "alert").length;
  const appBlocked = appPairs.filter((p) => p.worstState === "block").length;
  const appAlerting = appPairs.filter((p) => p.worstState === "alert").length;

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Rate limits">
        Each limit alerts the team on Slack at one threshold and blocks calls at another while enforcement is on. Global limits cap an instance&apos;s billable usage across all apps; per-app limits cap each integration, and apps without a row use the <code>*</code> defaults.
      </AdminPageIntro>

      <CurrentLimits windows={windows} updateWindow={updateWindow} onSaved={refresh} />

      {(blocked > 0 || alerting > 0 || appBlocked > 0 || appAlerting > 0) && (
        <div className={styles.attention}>
          {blocked > 0 && (
            <span className={`${styles.badge} ${styles.badgeLg} ${badgeClass("block")}`}>{blocked} blocked</span>
          )}
          {alerting > 0 && (
            <span className={`${styles.badge} ${styles.badgeLg} ${badgeClass("alert")}`}>{alerting} alerting</span>
          )}
          {appBlocked > 0 && (
            <span className={`${styles.badge} ${styles.badgeLg} ${badgeClass("block")}`}>
              {appBlocked} app-blocked
            </span>
          )}
          {appAlerting > 0 && (
            <span className={`${styles.badge} ${styles.badgeLg} ${badgeClass("alert")}`}>
              {appAlerting} app-alerting
            </span>
          )}
        </div>
      )}

      <PerAppLimits
        apps={apps}
        catalogSlugs={data?.catalogSlugs ?? []}
        updateAppWindow={updateAppWindow}
        deleteApp={deleteApp}
        onSaved={refresh}
      />

      {appPairs.length > 0 && (
        <section className={styles.limitsSection}>
          <h3 className={styles.sectionTitle}>Instances near a per-app limit</h3>
          <div className={styles.listSection}>
            <table
              className={`${styles.table} ${styles.pairsTable}${isFetching ? ` ${styles.tableFetching}` : ""}`}
            >
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>App</th>
                  {APP_EVAL_WINDOWS.map((w) => (
                    <th key={w} className={styles.num}>
                      {w}
                    </th>
                  ))}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {appPairs.map((p) => (
                  <AppPairRow key={`${p.tenantId}|${p.integrationSlug}`} pair={p} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className={styles.limitsSection}>
        <h3 className={styles.sectionTitle}>Instance totals</h3>
        <div className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Instance", "Owner", ...WINDOW_HEADERS, "Status"]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.errorPanel}>Couldn't load rate limits{error ? `: ${error}` : "."}</div>
        )}
        {status === "ready" && tenants.length === 0 && (
          <div className={styles.empty}>No metered tool-call usage in the last 30 days.</div>
        )}
        {status === "ready" && tenants.length > 0 && (
          <table className={isFetching ? `${styles.table} ${styles.tableFetching}` : styles.table}>
            <thead>
              <tr>
                <th>Instance</th>
                <th>Owner</th>
                {windows.map((w) => (
                  <th key={w.window} className={styles.num}>
                    {w.window}
                  </th>
                ))}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <TenantRow key={t.tenantId} tenant={t} />
              ))}
            </tbody>
          </table>
        )}
        </div>
      </section>
    </div>
  );
};

const CurrentLimits = ({
  windows,
  updateWindow,
  onSaved,
}: {
  windows: RateLimitWindowLimit[];
  updateWindow: (window: string, alert: number | null, block: number | null) => Promise<void>;
  onSaved: () => void;
}): JSX.Element => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, { alert: string; block: string }>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEdit = (): void => {
    const next: Record<string, { alert: string; block: string }> = {};
    for (const w of windows) {
      next[w.window] = {
        alert: w.alert === null ? "" : String(w.alert),
        block: w.block === null ? "" : String(w.block),
      };
    }
    setDraft(next);
    setSaveError(null);
    setEditing(true);
  };

  const setField = (window: string, tier: "alert" | "block", value: string): void =>
    setDraft((d) => ({ ...d, [window]: { ...(d[window] ?? { alert: "", block: "" }), [tier]: value } }));

  const parse = (v: string): number | null => {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? Math.round(n) : null;
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      for (const w of windows) {
        const d = draft[w.window];
        if (!d) continue;
        const alert = parse(d.alert);
        const block = parse(d.block);
        if (alert === w.alert && block === w.block) continue; // unchanged
        await updateWindow(w.window, alert, block);
      }
      setEditing(false);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.limitsSection}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Global limits</h3>
        {editing ? (
          <div className={styles.editActions}>
            {saveError && <span className={styles.saveError}>{saveError}</span>}
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="button" className={styles.saveBtn} onClick={() => void save()} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        ) : (
          windows.length > 0 && (
            <button type="button" className={styles.refreshBtn} onClick={startEdit}>
              Edit
            </button>
          )
        )}
      </div>
      {windows.length > 0 && (
        <div className={styles.limitGrid}>
          {windows.map((w) => (
            <div key={w.window} className={styles.limitItem}>
              <span className={styles.limitWindow}>{w.window}</span>
              <div className={styles.limitRow}>
                <span className={styles.limitRowLabel}>Alert</span>
                {editing ? (
                  <input
                    type="number"
                    min={1}
                    aria-label={`${w.window} alert limit`}
                    className={styles.limitInput}
                    value={draft[w.window]?.alert ?? ""}
                    onChange={(e) => setField(w.window, "alert", e.target.value)}
                  />
                ) : (
                  <span className={styles.alert}>{formatLimit(w.alert)}</span>
                )}
              </div>
              <div className={styles.limitRow}>
                <span className={styles.limitRowLabel}>Block</span>
                {editing ? (
                  <input
                    type="number"
                    min={1}
                    aria-label={`${w.window} block limit`}
                    className={styles.limitInput}
                    value={draft[w.window]?.block ?? ""}
                    onChange={(e) => setField(w.window, "block", e.target.value)}
                  />
                ) : (
                  <span className={styles.block}>{formatLimit(w.block)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// The per-app windows, in display order. '5m' is gateway-enforced (block only,
// no live counter to show); '1h'/'24h' are evaluator tiers.
const APP_ALL_WINDOWS: ReadonlyArray<string> = ["5m", "1h", "24h"];
const APP_EVAL_WINDOWS: ReadonlyArray<string> = ["1h", "24h"];

type AppDraft = Record<string, { alert: string; block: string }>;

const draftFrom = (app: AppRateLimit | null): AppDraft => {
  const next: AppDraft = {};
  for (const key of APP_ALL_WINDOWS) {
    const w = app?.windows.find((x) => x.window === key);
    next[key] = {
      alert: w?.alert == null ? "" : String(w.alert),
      block: w?.block == null ? "" : String(w.block),
    };
  }
  return next;
};

const parseTier = (v: string): number | null => {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n) : null;
};

const PerAppLimits = ({
  apps,
  catalogSlugs,
  updateAppWindow,
  deleteApp,
  onSaved,
}: {
  apps: AppRateLimit[];
  catalogSlugs: string[];
  updateAppWindow: (slug: string, window: string, alert: number | null, block: number | null) => Promise<void>;
  deleteApp: (slug: string) => Promise<void>;
  onSaved: () => void;
}): JSX.Element => {
  // editingSlug != "" = an existing row is being edited (one at a time).
  // pending = new apps staged via the permanent ghost row at the table foot;
  // several can be staged at once, each saved (or discarded) independently.
  const [editingSlug, setEditingSlug] = useState("");
  const [pending, setPending] = useState<Array<{ slug: string; draft: AppDraft }>>([]);
  const [draft, setDraft] = useState<AppDraft>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEdit = (app: AppRateLimit): void => {
    setDraft(draftFrom(app));
    setEditingSlug(app.integrationSlug);
    setSaveError(null);
  };

  const stagePending = (slug: string): void => {
    if (slug === "") return;
    setPending((p) => [...p, { slug, draft: draftFrom(null) }]);
    setSaveError(null);
  };

  const dropPending = (slug: string): void =>
    setPending((p) => p.filter((row) => row.slug !== slug));

  const setPendingField = (slug: string, window: string, tier: "alert" | "block", value: string): void =>
    setPending((p) =>
      p.map((row) =>
        row.slug === slug
          ? {
              ...row,
              draft: {
                ...row.draft,
                [window]: { ...(row.draft[window] ?? { alert: "", block: "" }), [tier]: value },
              },
            }
          : row,
      ),
    );

  const setField = (window: string, tier: "alert" | "block", value: string): void =>
    setDraft((d) => ({ ...d, [window]: { ...(d[window] ?? { alert: "", block: "" }), [tier]: value } }));

  // Writes one app's windows from a draft. `existing` = the app's current rows
  // (null for a staged-new app: only windows with at least one tier set are
  // written, so adding "metaads with a 1h limit" creates no empty 5m/24h rows).
  const persist = async (slug: string, d: AppDraft, existing: AppRateLimit | null): Promise<void> => {
    for (const key of APP_ALL_WINDOWS) {
      const w = d[key];
      if (!w) continue;
      const alert = key === "5m" ? null : parseTier(w.alert);
      const block = parseTier(w.block);
      const prior = existing?.windows.find((x) => x.window === key);
      const unchanged =
        existing !== null && alert === (prior?.alert ?? null) && block === (prior?.block ?? null);
      const skipEmptyNew = existing === null && alert === null && block === null;
      if (unchanged || skipEmptyNew) continue;
      await updateAppWindow(slug, key, alert, block);
    }
  };

  const saveEdit = async (): Promise<void> => {
    if (editingSlug === "") return;
    setBusy(true);
    setSaveError(null);
    try {
      await persist(editingSlug, draft, apps.find((a) => a.integrationSlug === editingSlug) ?? null);
      setEditingSlug("");
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const savePending = async (slug: string): Promise<void> => {
    const row = pending.find((p) => p.slug === slug);
    if (!row) return;
    setBusy(true);
    setSaveError(null);
    try {
      await persist(slug, row.draft, null);
      dropPending(slug);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug: string): Promise<void> => {
    if (!window.confirm(`Remove the per-app limits for '${slug}'? It falls back to the '*' defaults.`)) {
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      await deleteApp(slug);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const editCells = (
    labelPrefix: string,
    d: AppDraft,
    set: (window: string, tier: "alert" | "block", value: string) => void,
  ): JSX.Element[] =>
    APP_ALL_WINDOWS.flatMap((key) => [
      key === "5m" ? (
        <td key="5m-alert" className={`${styles.num} ${styles.groupStart}`}>
          <span className={styles.denom}>—</span>
        </td>
      ) : (
        <td key={`${key}-alert`} className={`${styles.num} ${styles.groupStart}`}>
          <input
            type="number"
            min={1}
            aria-label={`${labelPrefix} ${key} alert limit`}
            className={styles.limitInput}
            value={d[key]?.alert ?? ""}
            onChange={(e) => set(key, "alert", e.target.value)}
          />
        </td>
      ),
      <td key={`${key}-block`} className={styles.num}>
        <input
          type="number"
          min={1}
          aria-label={`${labelPrefix} ${key} block limit`}
          className={styles.limitInput}
          value={d[key]?.block ?? ""}
          onChange={(e) => set(key, "block", e.target.value)}
        />
      </td>,
    ]);

  // Unset tiers read as muted dashes, not tier-coloured ones; set tiers keep
  // the alert/block colour cue.
  const tierValue = (value: number | null | undefined, tone: "alert" | "block"): JSX.Element =>
    value == null ? (
      <span className={styles.denom}>—</span>
    ) : (
      <span className={tone === "alert" ? styles.alert : styles.block}>{formatLimit(value)}</span>
    );

  return (
    <section className={styles.limitsSection}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Per-app limits</h3>
        {saveError && <span className={styles.saveError}>{saveError}</span>}
      </div>
      <div className={styles.listSection}>
        <table className={`${styles.table} ${styles.appsTable}`}>
        <thead>
          <tr>
            <th rowSpan={2}>App</th>
            <th colSpan={2} className={`${styles.groupHead} ${styles.groupStart}`}>
              5m
            </th>
            <th colSpan={2} className={`${styles.groupHead} ${styles.groupStart}`}>
              1h
            </th>
            <th colSpan={2} className={`${styles.groupHead} ${styles.groupStart}`}>
              24h
            </th>
            <th rowSpan={2} />
          </tr>
          <tr>
            <th className={`${styles.num} ${styles.groupStart}`}>Alert</th>
            <th className={styles.num}>Block</th>
            <th className={`${styles.num} ${styles.groupStart}`}>Alert</th>
            <th className={styles.num}>Block</th>
            <th className={`${styles.num} ${styles.groupStart}`}>Alert</th>
            <th className={styles.num}>Block</th>
          </tr>
        </thead>
        <tbody>
          {apps.map((app) => {
            const slug = app.integrationSlug;
            const isEditing = editingSlug === slug;
            return (
              <tr key={slug}>
                <td className={styles.appSlug}>{slug === "*" ? "* (all other apps)" : slug}</td>
                {isEditing
                  ? editCells(slug, draft, setField)
                  : APP_ALL_WINDOWS.flatMap((key) => {
                      const w = app.windows.find((x) => x.window === key);
                      return [
                        <td key={`${key}-alert`} className={`${styles.num} ${styles.groupStart}`}>
                          {key === "5m" ? (
                            <span className={styles.denom}>—</span>
                          ) : (
                            tierValue(w?.alert, "alert")
                          )}
                        </td>,
                        <td key={`${key}-block`} className={styles.num}>
                          {tierValue(w?.block, "block")}
                        </td>,
                      ];
                    })}
                <td className={styles.rowActions}>
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        className={styles.refreshBtn}
                        onClick={() => setEditingSlug("")}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={() => void saveEdit()}
                        disabled={busy}
                      >
                        {busy ? "Saving…" : "Save"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={styles.refreshBtn}
                        onClick={() => startEdit(app)}
                        disabled={busy || editingSlug !== ""}
                      >
                        Edit
                      </button>
                      {slug !== "*" && (
                        <button
                          type="button"
                          className={styles.refreshBtn}
                          onClick={() => void remove(slug)}
                          disabled={busy || editingSlug !== ""}
                        >
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {pending.map((row) => (
            <tr key={row.slug}>
              <td className={styles.appSlug}>{row.slug}</td>
              {editCells(row.slug, row.draft, (w, t, v) => setPendingField(row.slug, w, t, v))}
              <td className={styles.rowActions}>
                <button
                  type="button"
                  className={styles.refreshBtn}
                  onClick={() => dropPending(row.slug)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.saveBtn}
                  onClick={() => void savePending(row.slug)}
                  disabled={busy}
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </td>
            </tr>
          ))}
          {/* The permanent ghost row: pick an app and it becomes a staged row
              above (with live inputs), and this template renews for the next.
              Catalog-constrained - a typo'd slug would silently limit nothing. */}
          <tr>
            <td>
              <select
                aria-label="new app slug"
                className={styles.addSelect}
                value=""
                onChange={(e) => stagePending(e.target.value)}
              >
                <option value="">Add an app…</option>
                {catalogSlugs
                  .filter(
                    (slug) =>
                      !apps.some((a) => a.integrationSlug === slug) &&
                      !pending.some((p) => p.slug === slug),
                  )
                  .map((slug) => (
                    <option key={slug} value={slug}>
                      {slug}
                    </option>
                  ))}
              </select>
            </td>
            {APP_ALL_WINDOWS.flatMap((key) => [
              <td key={`${key}-alert`} className={`${styles.num} ${styles.groupStart}`}>
                {key === "5m" ? (
                  <span className={styles.denom}>—</span>
                ) : (
                  <input type="number" className={styles.limitInput} disabled aria-hidden="true" />
                )}
              </td>,
              <td key={`${key}-block`} className={styles.num}>
                <input type="number" className={styles.limitInput} disabled aria-hidden="true" />
              </td>,
            ])}
            <td className={styles.rowActions} />
          </tr>
        </tbody>
        </table>
      </div>
    </section>
  );
};

const AppPairRow = ({ pair }: { pair: AppRateLimitPair }): JSX.Element => (
  <tr>
    <td>
      <div className={styles.instance}>
        <span className={styles.instanceName} title={pair.tenantName ?? pair.tenantId}>
          {pair.tenantName ?? pair.tenantId}
        </span>
        {pair.tenantName !== null && pair.tenantName !== pair.tenantId && (
          <span className={styles.instanceId} title={pair.tenantId}>
            {pair.tenantId}
          </span>
        )}
      </div>
    </td>
    <td className={styles.appSlug}>{pair.integrationSlug}</td>
    {pair.windows.map((w) => {
      const limit = w.block ?? w.alert;
      return (
        <td key={w.window} className={`${styles.num} ${textClass(w.state)}`}>
          {formatNumber(w.observed)}
          <span className={styles.denom}> / {limit === null ? "—" : formatNumber(limit)}</span>
        </td>
      );
    })}
    <td>
      <span className={`${styles.badge} ${badgeClass(pair.worstState)}`}>{STATE_LABEL[pair.worstState]}</span>
    </td>
  </tr>
);

const TenantRow = ({ tenant }: { tenant: RateLimitTenant }): JSX.Element => (
  <tr>
    <td>
      <div className={styles.instance}>
        <span className={styles.instanceName} title={tenant.tenantName ?? tenant.tenantId}>
          {tenant.tenantName ?? tenant.tenantId}
        </span>
        <span className={styles.instanceId} title={tenant.tenantId}>
          {tenant.tenantId}
        </span>
      </div>
    </td>
    <td className={styles.owner}>
      <span className={styles.clip} title={tenant.ownerEmail ?? undefined}>
        {tenant.ownerEmail ?? "—"}
      </span>
    </td>
    {tenant.windows.map((w) => {
      const limit = w.block ?? w.alert;
      return (
        <td key={w.window} className={`${styles.num} ${textClass(w.state)}`}>
          {formatNumber(w.observed)}
          {limit !== null && <span className={styles.denom}> / {formatNumber(limit)}</span>}
        </td>
      );
    })}
    <td>
      <span className={`${styles.badge} ${badgeClass(tenant.worstState)}`}>
        {STATE_LABEL[tenant.worstState]}
      </span>
    </td>
  </tr>
);
